import {
  ChatMessageType,
  ChatMessageWrapper,
  formatLongDateTime,
} from '@law-connect/react-components';
import {
  Message,
  MessageType,
  File as AMFile,
  Lawyer,
  LawyerConnectionUser,
} from '@law-connect/types';
import React, { FC, useCallback, useMemo, useState, useEffect } from 'react';
import style from './style.module.less';
import { formatDate } from '../../utils/format-date';
import { PermissionMessage } from './permission-message';
import { ClientIntakeMessage } from './client-intake-message';

import TickCircleIcon from './tick-circle.svg?react';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import selectors from '../../redux/selectors';
import { FileName, FilePreview } from '../../components/files/file-preview';
import { FileUploading } from '../../components/files/uploading-file';
import { UserAvatarComponent } from '../../components/user-avatar';
import { Typography } from '@galaxy-ds/core';
import { actions } from '../../redux/slices';
import { AUTH_STORAGE_KEY } from '../../constants';
import { jwtDecode } from 'jwt-decode';
import { useTranslation } from 'react-i18next';
const HOVER_DELAY = 300;
const DEFAULT_AVATAR_OFFSET = 88;

interface Props {
  connectionId: string;
  messages: Message[];
}

export const MessagesComponent: FC<Props> = ({ messages, connectionId }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  // going in reverse of the message list, we want to find the message.created at to be closes to connection.readAt
  const connection = useAppSelector(
    selectors.lawyerConnection.getConnectionById(connectionId)
  );
  const user = useAppSelector(selectors.lawyerConnection.getUser(connectionId));
  const isSendingFile = useAppSelector(selectors.file.isSendingFiles());
  const [hasReadUnreadBar, setHasReadUnreadBar] = React.useState(false);
  const [readUnreadText, setReadUnreadText] = React.useState('');
  const [hasBarReadTickIcon, setHasBarReadTickIcon] = React.useState(false);

  const lawyers = useAppSelector(
    selectors.lawyerConnection.getLawyersByIds(connectionId)
  );
  const isFetchingLawyers = useAppSelector(
    selectors.lawyerConnection.isFetchingMessages()
  );

  const lastReadLawyerMessageId: string = useMemo(() => {
    if (!connection) {
      return null;
    }
    const message = [...messages]
      .filter((m) => m.from === 'lawyer')
      .reverse()
      .find((f) => f.createdAt <= connection.readAt);

    // if no message is found or connection readAt is null,
    // we want to find the last message that was read by the lawyer
    if (!message) {
      const lastReadAtMessage = [...messages]
        .filter((m) => m.from === 'lawyer')
        .reverse()
        .find((f) => f.readAt);

      if (!lastReadAtMessage?.id) {
        const emailOpened =
          messages.length > 0 &&
          connection.emailOpenedAt >= messages[0].createdAt;
        if (
          messages.length === 1 ||
          (messages.length === 2 &&
            (messages[1].type === MessageType.Permission ||
              messages[1].type === MessageType.ContactInformation))
        ) {
          if (emailOpened) {
            // only show read via email message bar if there is only one message
            setHasReadUnreadBar(true);
            setReadUnreadText(
              t('lawyer.read-via-email') +
                ' ' +
                formatDate(connection.emailOpenedAt)
            );
            setHasBarReadTickIcon(true);
            return messages[0].id;
          } else {
            setHasReadUnreadBar(true);
            setReadUnreadText(t('lawyer.unread'));
          }
        } else if (messages.length > 1) {
          if (emailOpened) {
            // more than one message, but only the intro message has been read via email
            // return the intro message id to show that it has been read
            setHasReadUnreadBar(false);
            setHasBarReadTickIcon(false);
            setReadUnreadText('');
            return messages[0].id;
          } else {
            setHasReadUnreadBar(true);
            setReadUnreadText(t('lawyer.unread'));
          }
        }
      } else {
        if (
          messages.length === 1 ||
          (messages.length === 2 &&
            (messages[1].type === MessageType.Permission ||
              messages[1].type === MessageType.ContactInformation))
        ) {
          setHasReadUnreadBar(true);
          setReadUnreadText(
            t('lawyer.read') + ' ' + formatDate(lastReadAtMessage.readAt)
          );
          setHasBarReadTickIcon(true);
        } else {
          setHasReadUnreadBar(false);
          setHasBarReadTickIcon(false);
          setReadUnreadText('');
        }
      }
      return lastReadAtMessage?.id;
    }

    if (
      messages.length === 1 ||
      (messages.length === 2 &&
        (messages[1].type === MessageType.Permission ||
          messages[1].type === MessageType.ContactInformation))
    ) {
      setHasReadUnreadBar(true);
      setReadUnreadText(t('lawyer.read') + ' ' + formatDate(message?.readAt));
      setHasBarReadTickIcon(true);
    } else {
      setHasReadUnreadBar(false);
      setHasBarReadTickIcon(false);
      setReadUnreadText('');
    }

    return message?.id;
  }, [connection, messages, t]);

  useEffect(() => {
    if (!connection) {
      return;
    }

    if (!lawyers && !isFetchingLawyers) {
      // get the list of lawyers in messages
      const messagedLawyers = messages
        .flatMap((m) => {
          if (m.from === 'lawyer') {
            return m.lawyerId;
          }
        })
        .filter((value, index, self) => self.indexOf(value) === index);

      // check lawyers list and return those that do not exist in the current list
      const newLawyers = messagedLawyers.filter(
        (l) => !lawyers?.find((lawyer) => lawyer.id === l) && l !== undefined
      );

      // add connection lawyer id to the list of lawyers if it is not already in the list
      if (
        connection?.lawyer?.id &&
        !newLawyers.includes(connection?.lawyer?.id)
      ) {
        newLawyers.push(connection?.lawyer?.id);
      }

      if (newLawyers.length > 0) {
        dispatch(
          actions.lawyerConnection.fetchLawyersByIds({
            connectionId: connection.id,
            lawyerIds: newLawyers,
          })
        );
      }
    } else {
      // get last message and check if the last message is from a lawyer
      const lastMessage = messages[messages.length - 1];
      if (!lastMessage || lastMessage.from !== 'lawyer') {
        return;
      }

      // check if the lawyer is in the list of lawyers and if not fetch the lawyer
      const lawyerId = lastMessage.lawyerId;
      if (!lawyers?.find((l) => l.id === lawyerId)) {
        dispatch(
          actions.lawyerConnection.fetchLawyersByIds({
            connectionId: connection.id,
            lawyerIds: [lawyerId],
          })
        );
      }
    }
  }, [connection, messages, lawyers, dispatch, isFetchingLawyers]);

  const allFiles = useAppSelector(selectors.file.getFiles(connectionId));

  return (
    <div className={style.messageContainer}>
      {messages.map((message, index) => (
        <MessageWrapper
          key={message.id ?? index}
          connectionId={connectionId}
          messages={messages}
          message={message}
          lastReadLawyerMessage={message.id === lastReadLawyerMessageId}
          barReadDisplayed={hasBarReadTickIcon}
          files={allFiles?.chat ?? []}
          lawyers={lawyers}
          user={user}
          connectionLawyerId={connection?.lawyer?.id}
        />
      ))}
      {hasReadUnreadBar ? (
        <div className={style.readUnreadBar}>
          {hasBarReadTickIcon ? (
            <div className={style.readBarTickIconWrapper}>
              <TickCircleIcon className={style.readIcon} />
            </div>
          ) : null}
          {readUnreadText}
        </div>
      ) : null}
      <FileUploading
        filesLoading={isSendingFile}
        from={ChatMessageType.Sender}
      />
      {connection?.deletedAt || connection?.endedAt ? (
        <div className={style.endedAt}>
          <Typography variant='h2'>Conversation ended.</Typography>
        </div>
      ) : null}
    </div>
  );
};

interface MessageWrapperProps {
  connectionId: string;
  message: Message;
  messages: Message[];
  lastReadLawyerMessage?: boolean;
  barReadDisplayed?: boolean;
  files: AMFile[];
  user?: LawyerConnectionUser;
  lawyers?: Lawyer[];
  connectionLawyerId?: string;
}

const MessageWrapper: FC<MessageWrapperProps> = (props) => {
  // we need the list of messages to find the corresponding message for the permission message
  const {
    message,
    messages,
    connectionId,
    lastReadLawyerMessage,
    barReadDisplayed,
    files,
    lawyers,
    user,
    connectionLawyerId,
  } = props;
  const { t } = useTranslation();
  const [showTimestamp, setShowTimestamp] = React.useState(false);
  const timer = React.useRef<NodeJS.Timeout | null>(null);

  const avatarContainerRef = React.useRef<HTMLDivElement>(null);
  const [avatarApplyMargin, setAvatarApplyMargin] = useState(false);

  const startHover = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setShowTimestamp(true);
  }, []);
  const endHover = useCallback(() => {
    timer.current = setTimeout(() => {
      setShowTimestamp(false);
    }, HOVER_DELAY);
  }, []);

  const token = window.sessionStorage.getItem(AUTH_STORAGE_KEY) || '';
  // decode the token to get the lawyer id in sub
  const decodedToken = jwtDecode(token);

  const lawyer = useMemo(() => {
    if (message.from !== 'lawyer') {
      return undefined;
    }

    const lawyerId = message.lawyerId || connectionLawyerId;
    return lawyers?.find((lawyer) => lawyer.id === lawyerId);
  }, [message.lawyerId, connectionLawyerId, lawyers, message.from]);

  useEffect(() => {
    const checkHeight = () => {
      if (avatarContainerRef.current) {
        setAvatarApplyMargin(
          avatarContainerRef.current.offsetHeight > DEFAULT_AVATAR_OFFSET
        );
      }
    };

    checkHeight();
    window.addEventListener('resize', checkHeight);

    return () => window.removeEventListener('resize', checkHeight);
  }, []);

  const timestampComponent = useMemo(() => {
    return (
      <div
        className={`${style.timestamp} 
  ${showTimestamp ? style.show : style.hide}
  ${message.from === 'lawyer' ? style.right : style.left}`}
      >
        {formatDate(message.createdAt ?? Date.now())}
        {lastReadLawyerMessage ? (
          <div className={style.lastRead}>
            &nbsp;&nbsp;|&nbsp;&nbsp;{t('lawyer.read')}
            <TickCircleIcon className={style.readIcon} />
          </div>
        ) : null}
      </div>
    );
  }, [
    showTimestamp,
    message.from,
    message.createdAt,
    lastReadLawyerMessage,
    t,
  ]);

  const chatMessageTimestampComponent = useMemo(() => {
    return (
      <div className={`${style.timestampWrapper}`}>
        <div>{formatDate(message.createdAt ?? Date.now())}</div>
        {lastReadLawyerMessage ? (
          <div className={style.lastRead}>
            &nbsp;&nbsp;|&nbsp;&nbsp;{t('lawyer.read')}
            <TickCircleIcon className={style.readIcon} />
          </div>
        ) : null}
      </div>
    );
  }, [message.createdAt, lastReadLawyerMessage, t]);

  const senderName = useMemo(() => {
    if (message.from === 'lawyer') {
      const lawyerId = message.lawyerId || connectionLawyerId;
      const lawyer = lawyers?.find((l) => l.id === lawyerId);
      return lawyer ? `${lawyer.firstName} ${lawyer.lastName}` : null;
    }

    if (user) {
      const userName =  `${user.firstName || ''}${
        user.firstName?.length && user.lastName?.length ? ' ' : ''
      }${user.lastName || ''}`.trim() || 'Client';
      return userName;
    } else {
      return 'Client';
    }
  }, [message.from, message.lawyerId, user, connectionLawyerId, lawyers]);

  if (
    message.type === MessageType.Permission ||
    message.type === MessageType.ClientIntakeForm ||
    message.type === MessageType.ContactInformation
  ) {
    const MessageComponent =
      message.type === MessageType.Permission
        ? PermissionMessage
        : message.type === MessageType.ClientIntakeForm
          ? ClientIntakeMessage
          : message.type === MessageType.ContactInformation
            ? PermissionMessage
            : null;
    return (
      <div
        ref={avatarContainerRef}
        className={`${style.messageWrapper} 
        ${message.from === 'lawyer' ? style.sender : ''}`}
      >
        {lastReadLawyerMessage && !barReadDisplayed ? (
          <div className={style.readIconWrapper}>
            <TickCircleIcon className={style.readIcon} />
          </div>
        ) : (
          ''
        )}
        <MessageComponent
          messages={messages}
          message={message}
          connectionId={connectionId}
          avatar={
            message.from === 'lawyer' ? (
              <img
                src={lawyer?.image}
                alt={lawyer?.firstName}
                className={style.avatarIcon}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null;
                  //currentTarget.src = placeholderImage;
                }}
              />
            ) : (
              <div>
                <UserAvatarComponent
                  user={user}
                  userImageClassName={style.userImage}
                />
              </div>
            )
          }
          avatarClassName={`${
            message.from === 'lawyer'
              ? style.avatarLawyerContainer
              : style.avatarClientContainer
          }
          `}
          wrapperClassName={`${
            message.from === 'lawyer'
              ? style.wrapperEndContainer
              : style.wrapperStartContainer
          }`}
          sender={senderName ? <div>{senderName}</div> : null}
          timestamp={timestampComponent}
          lawyerFirstName={
            message.lawyerId !== decodedToken?.sub
              ? lawyers?.find((l) => l.id === message.lawyerId)?.firstName
              : undefined
          }
          endHover={endHover}
          startHover={startHover}
        />
      </div>
    );
  } else if (
    message.type === MessageType.Chat ||
    message.type === MessageType.Location ||
    message.type === MessageType.Confirmation
  ) {
    if (message.content) {
      return (
        <div
          ref={avatarContainerRef}
          className={`${style.chatMessageContainer} 
          ${message.from === 'lawyer' ? style.sender : ''}`}
        >
          {lastReadLawyerMessage && !barReadDisplayed ? (
            <div className={style.readMessageIconWrapper}>
              <TickCircleIcon className={style.readIcon} />
            </div>
          ) : (
            ''
          )}
          <ChatMessageWrapper
            messageBody={message.content}
            type={
              message.from === 'lawyer'
                ? ChatMessageType.Sender
                : ChatMessageType.Receiver
            }
            timestamp={chatMessageTimestampComponent}
            avatarRight={
              message.from === 'lawyer' ? (
                <img
                  src={lawyer?.image}
                  alt={lawyer?.firstName}
                  className={style.avatarIcon}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null;
                    //currentTarget.src = placeholderImage;
                  }}
                />
              ) : null
            }
            avatarLeft={
              message.from === 'client' ? (
                <div>
                  <UserAvatarComponent
                    user={user}
                    userImageClassName={style.userImage}
                  />
                </div>
              ) : null
            }
            avatarClassName={`${
              message.from === 'lawyer'
                ? style.avatarLawyerContainer
                : style.avatarClientContainer
            }
            `}
            sender={senderName ? <div>{senderName}</div> : null}
            className={`${style.chatMessageWrapper} ${
              message.from === 'lawyer' ? style.sender : style.receiver
            }`}
            receiverOverlayClassName={style.receiverOverlay}
            senderOverlayClassName={style.senderOverlay}
            wrapperClassName={`${
              message.from === 'lawyer'
                ? style.wrapperEndContainer
                : style.wrapperStartContainer
            }`}
          />
        </div>
      );
    } else if (message.fileIds?.length) {
      const messageFiles = message.fileIds.map((fileId) =>
        files.find((f) => f.id === fileId)
      );
      return (
        <div
          className={`${style.chatMessageContainer} 
          ${message.from === 'lawyer' ? style.sender : ''}`}
        >
          {lastReadLawyerMessage && !barReadDisplayed ? (
            <div className={style.readMessageIconWrapper}>
              <TickCircleIcon className={style.readIcon} />
            </div>
          ) : (
            ''
          )}
          <ChatMessageWrapper
            key={message.id}
            messageBody={message.content || ''}
            type={
              message.from === 'lawyer'
                ? ChatMessageType.Sender
                : ChatMessageType.Receiver
            }
            avatarRight={
              message.from === 'lawyer' ? (
                <img
                  src={lawyer?.image}
                  alt={lawyer?.firstName}
                  className={style.avatarIcon}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null;
                    //currentTarget.src = placeholderImage;
                  }}
                />
              ) : null
            }
            avatarLeft={
              message.from === 'client' ? (
                <div>
                  <UserAvatarComponent
                    user={user}
                    userImageClassName={style.userImage}
                  />
                </div>
              ) : null
            }
            avatarClassName={
              message.from === 'lawyer'
                ? style.avatarLawyerContainer
                : style.avatarClientContainer
            }
            sender={senderName ? <div>{senderName}</div> : null}
            timestamp={chatMessageTimestampComponent}
            files={messageFiles}
            className={`${style.fileWrapper} ${
              message.from === 'lawyer' ? style.sender : style.receiver
            }`}
            fileClassName={style.fileClassName}
            wrapperClassName={`${
              message.from === 'lawyer'
                ? style.wrapperEndContainer
                : style.wrapperStartContainer
            }`}
            filePreviewClassName={style.filePreviewBody}
            receiverOverlayClassName={style.receiverOverlay}
            senderOverlayClassName={style.senderOverlay}
            fixedPositioning={true}
          />
        </div>
      );
    }
  }
};
